import { listClasses, Paper, styled } from '@mui/material';
import {
    DateRangePicker as MuiDateRangePicker,
    DateRangePickerProps as MuiDateRangePickerProps,
    pickersLayoutClasses
} from '@mui/x-date-pickers-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'shared/hooks';

import CustomRangeShortcuts from './CustomRangeShortcuts';
import { DateRangePickerShortcutKey, getDateRangePickerShortcuts } from './dateRangePickerShortcuts';

export interface DateRangePickerProps extends Omit<MuiDateRangePickerProps, 'slots' | 'slotProps'> {
    error?: string;
    shortcutsKey?: DateRangePickerShortcutKey;
}

export default function DateRangePicker({
    error,
    shortcutsKey,
    ...rest 
}: DateRangePickerProps) {
    const { isExtraLargeDesktop, isMobile } = useMediaQuery();
    const { t } = useTranslation('common');

    const shortcuts = useMemo(() => {
        if (!shortcutsKey) {
            return undefined;
        }

        return getDateRangePickerShortcuts(shortcutsKey, isMobile, t);
    }, [ shortcutsKey, t, isMobile ]);

    return (
        <MuiDateRangePicker
            { ...rest }

            slots={{
                shortcuts: isMobile ? CustomRangeShortcuts : undefined,
                mobilePaper: MobilePaper
            }}

            slotProps={{
                shortcuts: shortcuts?.length ? {
                    items: shortcuts,
                    changeImportance: 'set'
                } : undefined,
                textField: (params) => ({
                    ...params,
                    error: Boolean(error),
                    helperText: error,
                    size: 'small'
                }),
                mobilePaper: {
                    withShortcuts: Boolean(shortcuts?.length)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any,
                popper: isExtraLargeDesktop ? undefined : {
                    placement: 'auto',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [ 0, -48 ]
                            }
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'viewport',
                                padding: 8,
                                altAxis: true
                            }
                        }
                    ]
                }
            }}
        />
    );
}

const MobilePaper = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'customProp'
})<{ withShortcuts: boolean }>(({ withShortcuts }) => ({
    ...(withShortcuts && {
        [`.${ pickersLayoutClasses.root } .${ listClasses.root }`]: {
            gridRow: 2,
            gridColumn: '2/4'
        },

        [`.${ pickersLayoutClasses.root } .${ pickersLayoutClasses.contentWrapper }`]: {
            gridRow: 3
        },

        [`.${ pickersLayoutClasses.root } .${ pickersLayoutClasses.actionBar }`]: {
            gridRow: 4
        }
    })
})) as unknown as typeof Paper;
